export type KnowledgeNodeId = string;

export interface KnowledgeNode {
  readonly id: KnowledgeNodeId;
  readonly title: string;
  readonly summary: string;
  readonly links: readonly KnowledgeNodeId[];
  readonly ageLevel?: number;
}

export type KnowledgeState = "discovered" | "partiallyUnderstood" | "understood";

export type KnowledgeProgress = Readonly<Record<KnowledgeNodeId, KnowledgeState>>;

/** Static content: all known nodes, keyed by id. */
export class KnowledgeCatalog {
  private readonly nodes = new Map<KnowledgeNodeId, KnowledgeNode>();

  register(node: KnowledgeNode): void {
    this.nodes.set(node.id, node);
  }

  get(id: KnowledgeNodeId): KnowledgeNode | undefined {
    return this.nodes.get(id);
  }

  all(): readonly KnowledgeNode[] {
    return [...this.nodes.values()];
  }
}

type DiscoveryListener = (nodeId: KnowledgeNodeId, state: KnowledgeState) => void;

/** Per-player runtime progress through the knowledge catalog. */
export class KnowledgeGraph {
  private listeners: DiscoveryListener[] = [];

  constructor(
    private readonly catalog: KnowledgeCatalog,
    private progress: KnowledgeProgress = {},
  ) {}

  getProgress(): KnowledgeProgress {
    return this.progress;
  }

  loadProgress(progress: KnowledgeProgress): void {
    this.progress = progress;
  }

  stateOf(nodeId: KnowledgeNodeId): KnowledgeState | undefined {
    return this.progress[nodeId];
  }

  onDiscovered(listener: DiscoveryListener): () => void {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter((l) => l !== listener);
    };
  }

  unlock(nodeId: KnowledgeNodeId, state: KnowledgeState = "discovered"): void {
    const node = this.catalog.get(nodeId);
    if (!node) throw new Error(`Unknown knowledge node: ${nodeId}`);
    const previous = this.progress[nodeId];
    if (previous === state) return;
    this.progress = { ...this.progress, [nodeId]: state };
    for (const listener of this.listeners) listener(nodeId, state);
  }
}
